import { useAuthStore } from "@/stores/authStore";
import { ApiError } from "@/lib/errors";
import i18n from "@/lib/i18n";

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const { basicAuthHeader } = useAuthStore.getState();

  const res = await fetch(`${BASE_URL}${path}`, {
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": i18n.language,
      ...(basicAuthHeader ? { Authorization: `Basic ${basicAuthHeader}` } : {}),
      ...init?.headers,
    },
    ...init,
  });

  if (res.status === 401 && basicAuthHeader) {
    useAuthStore.getState().logout();
  }

  if (!res.ok) {
    // Error bodies are a stable error code string (see the *Controller @ExceptionHandler methods),
    // not JSON — fall back to statusText only if the body itself is empty. extractMessage() maps
    // the code to a translated string; unmapped codes fall back to a generic message.
    const text = await res.text().catch(() => "");
    throw new ApiError(res.status, text || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const apiClient = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "PUT",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
  delete: (path: string, body?: unknown) =>
    request<void>(path, {
      method: "DELETE",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
};
